export const antennaDpTypeDefs = `

scalar AWSDateTime

scalar AWSJSON

type AntennaConfig {
  antennaName: String!
  capabilities: [CapabilityState!]!
  isKnownAntenna: Boolean!
  lastUpdatedTimestamp: String!
}

type ArchivedDeviceEventsQueryResponse {
  error: String
  queryExecutionId: String
}

type ArchivedDeviceEventsResultResponse {
  error: String
  events: [AWSJSON]
  nextToken: String
  status: String
}

type CachedDeviceEventsResponse {
  error: String
  events: [AWSJSON]
  totalCount: Int
}

type CapabilityState {
  capabilityId: String!
  capabilityName: String!
  devices: [DeviceState!]!
  status: String!
}

input CreateInventoryOrderRequestInput {
  """
  Client-supplied idempotency token. Retrying a create with the same token
  must not create a duplicate record; the resolver enforces this with a
  conditional write keyed on the token.
  """
  clientToken: String
  items: [InventoryOrderRequestItemInput!]
  justification: String
  priority: String
  requester: String!
}

input CreateInventoryPartInput {
  antennaCompatibility: String
  category: String

  """
  Client-supplied idempotency token. Retrying a create with the same token
  must not create a duplicate record; the resolver enforces this with a
  conditional write keyed on the token.
  """
  clientToken: String
  description: String!
  dimensions: String
  ipn: String
  manufacturerPartNumber: String
  notes: String
  priority: String
  supersededByPartId: String
  unitCost: Float
  viasatPartNumber: String
  weight: String
}

input CreateInventoryPartInstanceInput {
  """
  Client-supplied idempotency token. Retrying a create with the same token
  must not create a duplicate record; the resolver enforces this with a
  conditional write keyed on the token.
  """
  clientToken: String
  destinationSite: String
  installDate: AWSDateTime
  internalTicketLink: String
  location: InventoryLocation!
  notes: String
  partId: String!
  quantity: Int
  rmaDate: AWSDateTime
  rmaNumber: String
  serialNumber: String
  siteCode: String!
  slotPosition: String
  status: InventoryPartInstanceStatus
  ticketLink: String
}

input CreateInventoryPurchaseOrderInput {
  """
  Client-supplied idempotency token. Retrying a create with the same token
  must not create a duplicate record; the resolver enforces this with a
  conditional write keyed on the token.
  """
  clientToken: String
  expectedDate: AWSDateTime
  items: [InventoryPurchaseOrderItemInput!]
  notes: String
  orderDate: AWSDateTime
  poLink: String
  poNumber: String!
  status: InventoryPurchaseOrderStatus
  vendorName: String
}

input CreateInventoryRmaCycleInput {
  """
  Client-supplied idempotency token. Retrying a create with the same token
  must not create a duplicate record; the resolver enforces this with a
  conditional write keyed on the token.
  """
  clientToken: String
  createdDate: AWSDateTime
  instanceId: String!
  notes: String
  rmaNumber: String
  status: InventoryRmaStatus
  vendorName: String
}

input CreateInventorySiteInput {
  active: Boolean
  address: String
  antennaType: String

  """
  Client-supplied idempotency token. Retrying a create with the same token
  must not create a duplicate record; the resolver enforces this with a
  conditional write keyed on the token.
  """
  clientToken: String
  coordinates: String
  name: String
  notes: String

  """
  Natural key: a site is identified operationally by its station code (e.g. PDX,
  SIN, ICN), not a service-minted id - operators, tickets, and the legacy system all
  key on it, so the caller supplies it rather than the service generating an opaque
  id. Uniqueness is enforced by the resolver's conditional create on the token/key.
  """
  siteCode: String!
  siteType: InventorySiteType
}

input CreateInventoryVendorInput {
  """
  Client-supplied idempotency token. Retrying a create with the same token
  must not create a duplicate record; the resolver enforces this with a
  conditional write keyed on the token.
  """
  clientToken: String
  contactEmail: String
  contactName: String
  contactPhone: String
  name: String!
  notes: String
}

input DeleteInventoryPartInstanceInput {
  instanceId: String!
}

type DeviceConfig {
  antennaName: String!
  deviceInfoEntries: [DeviceInfoDataEntry!]!
  lastUpdatedTimestamp: String!
}

type DeviceInfoDataEntry {
  data: [StringMapEntry!]!
  deviceId: String!
  deviceInfoDataType: String!
}

type DeviceState {
  deviceId: String!
  status: String!
}

type DeviceTelemetryBatch {
  antennaName: String!
  events: [DeviceTelemetryEvent!]!
}

type DeviceTelemetryEvent {
  deviceId: String!
  payload: AWSJSON!
  tableName: String!
  timestamp: String!
}

input DeviceTelemetryEventInput {
  deviceId: String!
  payload: AWSJSON!
  tableName: String!
  timestamp: String!
}

type DxState {
  connectionId: String!

  """1 = up, 0 = down, absent = no datapoint in the window."""
  state: Float
}

input GetAntennaConfigInput {
  antennaName: String
}

input GetArchivedDeviceEventsQueryInput {
  antennaName: String!
  deviceId: String!
  endTime: Long!
  startTime: Long!
  tableName: String!
}

input GetArchivedDeviceEventsResultInput {
  nextToken: String
  queryExecutionId: String!
}

input GetCachedDeviceEventsInput {
  antennaName: String!
  deviceId: String!
  endTime: Long
  limit: Int
  offset: Int
  startTime: Long
}

input GetDeviceConfigInput {
  antennaName: String
}

input GetInventoryChangeHistoryEntryInput {
  changeId: String
}

input GetInventoryOrderRequestInput {
  orderRequestId: String
}

input GetInventoryPartInput {
  partId: String
}

input GetInventoryPartInstanceInput {
  instanceId: String
}

input GetInventoryPurchaseOrderInput {
  purchaseOrderId: String
}

input GetInventoryRmaCycleInput {
  rmaCycleId: String
}

input GetInventorySiteInput {
  siteCode: String
}

input GetInventoryVendorInput {
  vendorId: String
}

input GetLatestDeviceSnapshotInput {
  antennaName: String!
  deviceId: String!
}

input GetNetworkDeviceStatusInput {
  antennaName: String
}

input GetNetworkLinkStatusInput {
  antennaName: String
}

input GetPostPassReportInput {
  contactId: String
}

input GetTransformerStatusInput {
  antennaName: String
}

enum InventoryChangeAction {
  CREATE
  DELETE
  STATUS_UPDATE
  UPDATE
}

enum InventoryChangeEntityType {
  ORDER_REQUEST
  PART
  PART_INSTANCE
  PURCHASE_ORDER
  PURCHASE_ORDER_ITEM
  RMA_CYCLE
  SITE
  VENDOR
}

type InventoryChangeHistoryEntry {
  action: InventoryChangeAction!
  changeId: String!

  """JSON-encoded description of the change."""
  changes: String

  """The entity type that changed."""
  entityType: InventoryChangeEntityType!

  """The identifier of the changed record."""
  recordId: String!
  timestamp: AWSDateTime!

  """The user who made the change."""
  user: String
}

type InventoryDeleteResult {
  deleted: Boolean!
  id: String!
}

enum InventoryLocation {
  ANT_1
  ANT_2
  RMA
  SPARE
}

type InventoryOrderRequest {
  items: [InventoryOrderRequestItem!]
  justification: String
  orderRequestId: String!

  """
  Priority for review queue ordering. Free-text: legacy order requests carry
  values outside the enum vocabulary, and this field is informational only.
  """
  priority: String

  """Linked purchase order id, once the request is converted to a PO."""
  purchaseOrderId: String
  requester: String!
  reviewNotes: String
  reviewedAt: AWSDateTime
  reviewer: String
  status: InventoryOrderRequestStatus!
  submittedAt: AWSDateTime
}

type InventoryOrderRequestItem {
  customDescription: String
  destinationSite: String
  itemId: String!
  partId: String
  quantity: Int!
}

input InventoryOrderRequestItemInput {
  customDescription: String
  destinationSite: String
  partId: String
  quantity: Int!
}

enum InventoryOrderRequestStatus {
  APPROVED
  CANCELLED
  ORDERED
  REJECTED
  SUBMITTED
}

type InventoryPageInfo {
  hasNextPage: Boolean!
  nextToken: String
}

type InventoryPart {
  """
  Antenna compatibility (e.g. 5.4M, 7.3M, Both). Free-text: values are not
  enum-member-safe (leading digits, periods).
  """
  antennaCompatibility: String

  """
  Category (e.g. Spare, Critical). Free-text: the category vocabulary is open.
  """
  category: String
  createdAt: AWSDateTime
  description: String!

  """Physical dimensions (e.g. '23x23x16')."""
  dimensions: String

  """Internal part number."""
  ipn: String

  """Manufacturer part number."""
  manufacturerPartNumber: String
  notes: String

  """
  Stable part identifier (legacy integer id for migrated parts; ULID for new parts).
  """
  partId: String!

  """
  Priority for spares reconciliation (e.g. Standard, Critical). Free-text:
  migrated parts store title-case values outside the enum vocabulary, and
  the resolver treats this field as an opaque string.
  """
  priority: String

  """
  Part id of the replacement part, for end-of-life supersession tracking.
  """
  supersededByPartId: String

  """Unit cost, if known."""
  unitCost: Float
  updatedAt: AWSDateTime

  """Viasat part number."""
  viasatPartNumber: String

  """Weight (e.g. '33 lbs')."""
  weight: String
}

type InventoryPartInstance {
  createdAt: AWSDateTime

  """Destination site for in-transit instances."""
  destinationSite: String
  installDate: AWSDateTime
  instanceId: String!

  """Internal ticket link."""
  internalTicketLink: String
  location: InventoryLocation!
  notes: String

  """The catalog part this is an instance of."""
  partId: String!
  quantity: Int
  rmaDate: AWSDateTime
  rmaNumber: String
  serialNumber: String

  """Site code where the instance is located."""
  siteCode: String!

  """Slot position (e.g. NBSN Slot 1, Servo Amp A12, Motor X-Axis 1)."""
  slotPosition: String
  status: InventoryPartInstanceStatus

  """External (Sev-2) ticket link."""
  ticketLink: String
  updatedAt: AWSDateTime
}

enum InventoryPartInstanceStatus {
  ACTIVE
  AT_VENDOR
  AWAITING_SHIPMENT
  IN_TRANSIT
  ON_ORDER
  RECEIVED
  RETIRED
  SHIPPED_BACK
  SHIPPED_TO_VENDOR
}

enum InventoryPriority {
  CRITICAL
  LOW
  STANDARD
}

type InventoryPurchaseOrder {
  createdAt: AWSDateTime
  expectedDate: AWSDateTime
  items: [InventoryPurchaseOrderItem!]
  notes: String
  orderDate: AWSDateTime
  poLink: String
  poNumber: String!
  purchaseOrderId: String!
  status: InventoryPurchaseOrderStatus!
  updatedAt: AWSDateTime
  vendorName: String
}

type InventoryPurchaseOrderItem {
  """Free-text description when no catalog part applies."""
  customDescription: String
  destinationSite: String
  itemId: String!
  notes: String

  """Catalog part id, if this line references a catalog part."""
  partId: String
  quantity: Int!
  quantityReceived: Int
  status: InventoryPurchaseOrderStatus
}

input InventoryPurchaseOrderItemInput {
  customDescription: String
  destinationSite: String

  """Provide to update an existing line item; omit to add a new one."""
  itemId: String
  notes: String
  partId: String
  quantity: Int!
  quantityReceived: Int
  status: InventoryPurchaseOrderStatus
}

enum InventoryPurchaseOrderStatus {
  CANCELLED
  OPEN
  PARTIAL
  RECEIVED
}

enum InventoryReviewDecision {
  APPROVED
  ORDERED
  REJECTED
}

type InventoryRmaCycle {
  atVendorDate: AWSDateTime
  createdDate: AWSDateTime

  """The part instance this RMA cycle tracks."""
  instanceId: String!
  notes: String
  receivedDate: AWSDateTime
  rmaCycleId: String!
  rmaNumber: String
  shippedBackDate: AWSDateTime
  shippedToVendorDate: AWSDateTime
  status: InventoryRmaStatus
  vendorName: String
}

enum InventoryRmaStatus {
  AT_VENDOR
  CLOSED
  OPEN
  RECEIVED
  SHIPPED_BACK
  SHIPPED_TO_VENDOR
}

type InventorySite {
  """Whether the site is active."""
  active: Boolean
  address: String

  """Antenna type at the site."""
  antennaType: String

  """Coordinates as 'lat,lon'."""
  coordinates: String
  createdAt: AWSDateTime
  name: String
  notes: String

  """Site code (e.g. PDX, SIN, ICN)."""
  siteCode: String!

  """
  Site type (e.g. antenna, storage, lab). Determines whether the site
  participates in spares reconciliation (only antenna sites do).
  """
  siteType: InventorySiteType
  updatedAt: AWSDateTime
}

enum InventorySiteType {
  ANTENNA
  LAB
  STORAGE
}

type InventoryVendor {
  contactEmail: String
  contactName: String
  contactPhone: String
  createdAt: AWSDateTime
  name: String!
  notes: String
  updatedAt: AWSDateTime
  vendorId: String!
}

type LatestDeviceSnapshotResponse {
  error: String
  payload: AWSJSON
}

input ListInventoryChangeHistoryEntryInput {
  entityType: String
  limit: Int
  nextToken: String
  recordId: String
}

type ListInventoryChangeHistoryEntryOutput {
  pageInfo: InventoryPageInfo!
  results: [InventoryChangeHistoryEntry!]!
}

input ListInventoryOrderRequestInput {
  limit: Int
  nextToken: String
  status: String
}

type ListInventoryOrderRequestOutput {
  pageInfo: InventoryPageInfo!
  results: [InventoryOrderRequest!]!
}

input ListInventoryPartInput {
  limit: Int
  nextToken: String
}

input ListInventoryPartInstanceInput {
  limit: Int
  nextToken: String
  siteCode: String
}

type ListInventoryPartInstanceOutput {
  pageInfo: InventoryPageInfo!
  results: [InventoryPartInstance!]!
}

type ListInventoryPartOutput {
  pageInfo: InventoryPageInfo!
  results: [InventoryPart!]!
}

input ListInventoryPurchaseOrderInput {
  limit: Int
  nextToken: String
  status: String
}

type ListInventoryPurchaseOrderOutput {
  pageInfo: InventoryPageInfo!
  results: [InventoryPurchaseOrder!]!
}

input ListInventoryRmaCycleInput {
  limit: Int
  nextToken: String
  status: String
}

type ListInventoryRmaCycleOutput {
  pageInfo: InventoryPageInfo!
  results: [InventoryRmaCycle!]!
}

input ListInventorySiteInput {
  limit: Int
  nextToken: String
}

type ListInventorySiteOutput {
  pageInfo: InventoryPageInfo!
  results: [InventorySite!]!
}

input ListInventoryVendorInput {
  limit: Int
  nextToken: String
}

type ListInventoryVendorOutput {
  pageInfo: InventoryPageInfo!
  results: [InventoryVendor!]!
}

scalar Long

input ManageInventoryOrderRequestInput {
  """Review decision."""
  decision: InventoryReviewDecision!
  orderRequestId: String!

  """Purchase order the request was converted into, if approved/ordered."""
  purchaseOrderId: String
  reviewNotes: String
  reviewer: String!
}

type Mutation {
  createInventoryOrderRequest(cazJustification: String, input: CreateInventoryOrderRequestInput!): InventoryOrderRequest
  createInventoryPart(cazJustification: String, input: CreateInventoryPartInput!): InventoryPart
  createInventoryPartInstance(cazJustification: String, input: CreateInventoryPartInstanceInput!): InventoryPartInstance
  createInventoryPurchaseOrder(cazJustification: String, input: CreateInventoryPurchaseOrderInput!): InventoryPurchaseOrder
  createInventoryRmaCycle(cazJustification: String, input: CreateInventoryRmaCycleInput!): InventoryRmaCycle
  createInventorySite(cazJustification: String, input: CreateInventorySiteInput!): InventorySite
  createInventoryVendor(cazJustification: String, input: CreateInventoryVendorInput!): InventoryVendor
  deleteInventoryPartInstance(cazJustification: String, input: DeleteInventoryPartInstanceInput!): InventoryDeleteResult
  manageInventoryOrderRequest(cazJustification: String, input: ManageInventoryOrderRequestInput!): InventoryOrderRequest
  publishDeviceTelemetryBatch(input: PublishDeviceTelemetryBatchInput!): DeviceTelemetryBatch
  updateInventoryPart(cazJustification: String, input: UpdateInventoryPartInput!): InventoryPart
  updateInventoryPartInstance(cazJustification: String, input: UpdateInventoryPartInstanceInput!): InventoryPartInstance
  updateInventoryPurchaseOrder(cazJustification: String, input: UpdateInventoryPurchaseOrderInput!): InventoryPurchaseOrder
  updateInventoryRmaCycle(cazJustification: String, input: UpdateInventoryRmaCycleInput!): InventoryRmaCycle
  updateInventorySite(cazJustification: String, input: UpdateInventorySiteInput!): InventorySite
  updateInventoryVendor(cazJustification: String, input: UpdateInventoryVendorInput!): InventoryVendor
}

type NetworkDeviceState {
  """1 = up, 0 = down, absent = no datapoints in the window."""
  availability: Float

  """
  Full NetVane name, e.g. 'cmh51-a00-gs-edg-r101', the join key for NetVane and networking tooling.
  """
  deviceName: String!

  """Edge / Agg / Transformer."""
  switchType: String!
}

type NetworkDeviceStatus {
  antennaName: String!
  lastUpdatedTimestamp: String!
  switches: [NetworkDeviceState!]!
}

type NetworkLinkStatus {
  antennaName: String!

  """
  False for legacy sites whose DX metrics are not visible from the mgmt account, an empty dxStateList is then expected.
  """
  dxExpected: Boolean!
  dxStateList: [DxState!]!
  lastUpdatedTimestamp: String!

  """
  True for colo sites, an empty vpnTunnelStateList is then a visibility gap, not expected absence.
  """
  vpnExpected: Boolean!
  vpnTunnelStateList: [VpnTunnelState!]!
}

type PostPassReport {
  contactId: String!

  """
  MIME content type of the artifact (e.g. 'application/json', 'text/html', 'image/svg+xml').
  """
  contentType: String

  """
  Optional artifact path within the contact's report prefix.
  If not specified, the service uses 'report.json'.
  """
  path: String

  """Report availability status."""
  status: PostPassReportStatus!

  """
  Presigned S3 URL (5-minute expiry). Null if the report is not available.
  """
  url: String
}

enum PostPassReportStatus {
  AVAILABLE
  IN_PROGRESS
  NOT_FOUND
}

input PublishDeviceTelemetryBatchInput {
  antennaName: String!
  events: [DeviceTelemetryEventInput!]!
}

type Query {
  getAntennaConfig(cazJustification: String, input: GetAntennaConfigInput!): AntennaConfig
  getArchivedDeviceEventsQuery(input: GetArchivedDeviceEventsQueryInput!): ArchivedDeviceEventsQueryResponse
  getArchivedDeviceEventsResult(input: GetArchivedDeviceEventsResultInput!): ArchivedDeviceEventsResultResponse
  getCachedDeviceEvents(input: GetCachedDeviceEventsInput!): CachedDeviceEventsResponse
  getDeviceConfig(cazJustification: String, input: GetDeviceConfigInput!): DeviceConfig
  getInventoryChangeHistoryEntry(cazJustification: String, input: GetInventoryChangeHistoryEntryInput!): InventoryChangeHistoryEntry
  getInventoryOrderRequest(cazJustification: String, input: GetInventoryOrderRequestInput!): InventoryOrderRequest
  getInventoryPart(cazJustification: String, input: GetInventoryPartInput!): InventoryPart
  getInventoryPartInstance(cazJustification: String, input: GetInventoryPartInstanceInput!): InventoryPartInstance
  getInventoryPurchaseOrder(cazJustification: String, input: GetInventoryPurchaseOrderInput!): InventoryPurchaseOrder
  getInventoryRmaCycle(cazJustification: String, input: GetInventoryRmaCycleInput!): InventoryRmaCycle
  getInventorySite(cazJustification: String, input: GetInventorySiteInput!): InventorySite
  getInventoryVendor(cazJustification: String, input: GetInventoryVendorInput!): InventoryVendor
  getLatestDeviceSnapshot(input: GetLatestDeviceSnapshotInput!): LatestDeviceSnapshotResponse
  getNetworkDeviceStatus(cazJustification: String, input: GetNetworkDeviceStatusInput!): NetworkDeviceStatus
  getNetworkLinkStatus(cazJustification: String, input: GetNetworkLinkStatusInput!): NetworkLinkStatus
  getPostPassReport(cazJustification: String, input: GetPostPassReportInput!): PostPassReport
  getTransformerStatus(cazJustification: String, input: GetTransformerStatusInput!): TransformerStatus
  listInventoryChangeHistoryEntry(input: ListInventoryChangeHistoryEntryInput!): ListInventoryChangeHistoryEntryOutput
  listInventoryOrderRequest(input: ListInventoryOrderRequestInput!): ListInventoryOrderRequestOutput
  listInventoryPart(input: ListInventoryPartInput!): ListInventoryPartOutput
  listInventoryPartInstance(input: ListInventoryPartInstanceInput!): ListInventoryPartInstanceOutput
  listInventoryPurchaseOrder(input: ListInventoryPurchaseOrderInput!): ListInventoryPurchaseOrderOutput
  listInventoryRmaCycle(input: ListInventoryRmaCycleInput!): ListInventoryRmaCycleOutput
  listInventorySite(input: ListInventorySiteInput!): ListInventorySiteOutput
  listInventoryVendor(input: ListInventoryVendorInput!): ListInventoryVendorOutput
}

type StringMapEntry {
  key: String
  value: String
}

type Subscription {
  onDeviceTelemetry(antennaName: String!): DeviceTelemetryBatch
}

type TransformerAlarm {
  name: String!

  """
  OK / ALARM / INSUFFICIENT_DATA, or MISSING when the alarm does not exist.
  """
  state: String!
}

type TransformerCapabilityAssignment {
  capabilityNames: [String!]!

  """'name:port' form."""
  ingressAddress: String!
  isEgress: Boolean!
  priority: Int!

  """
  ACTIVE / ACTIVATING / DEACTIVATING, or UNKNOWN when the source omitted the field.
  """
  state: String!
}

type TransformerState {
  alarms: [TransformerAlarm!]!
  capabilities: [TransformerCapabilityAssignment!]!

  """
  Set when this host's capability fetch failed, distinct from an empty capabilities list.
  """
  capabilitiesError: String
  instanceId: String!

  """Online / ConnectionLost / Inactive, or UNKNOWN when not registered."""
  ssmPingStatus: String!

  """
  ACTIVE / INACTIVE / ISOLATED, HANDOFF_PENDING for an expected host absent from SSM, or UNKNOWN when the source omitted the field.
  """
  status: String!
  statusReason: String!
  transformerName: String!
}

type TransformerStatus {
  """
  Set when the alarm fetch failed, every host's alarms list is then empty.
  """
  alarmsError: String
  antennaName: String!
  lastUpdatedTimestamp: String!
  transformers: [TransformerState!]!
}

input UpdateInventoryPartInput {
  antennaCompatibility: String
  category: String
  description: String
  dimensions: String
  ipn: String
  manufacturerPartNumber: String
  notes: String
  partId: String!
  priority: String
  supersededByPartId: String
  unitCost: Float
  viasatPartNumber: String
  weight: String
}

input UpdateInventoryPartInstanceInput {
  destinationSite: String
  installDate: AWSDateTime
  instanceId: String!
  internalTicketLink: String
  location: InventoryLocation
  notes: String
  partId: String
  quantity: Int
  rmaDate: AWSDateTime
  rmaNumber: String
  serialNumber: String
  siteCode: String
  slotPosition: String
  status: InventoryPartInstanceStatus
  ticketLink: String
}

input UpdateInventoryPurchaseOrderInput {
  expectedDate: AWSDateTime
  items: [InventoryPurchaseOrderItemInput!]
  notes: String
  orderDate: AWSDateTime
  poLink: String
  poNumber: String
  purchaseOrderId: String!
  status: InventoryPurchaseOrderStatus
  vendorName: String
}

input UpdateInventoryRmaCycleInput {
  atVendorDate: AWSDateTime
  notes: String
  receivedDate: AWSDateTime
  rmaCycleId: String!
  rmaNumber: String
  shippedBackDate: AWSDateTime
  shippedToVendorDate: AWSDateTime
  status: InventoryRmaStatus
  vendorName: String
}

input UpdateInventorySiteInput {
  active: Boolean
  address: String
  antennaType: String
  coordinates: String
  name: String
  notes: String
  siteCode: String!
  siteType: InventorySiteType
}

input UpdateInventoryVendorInput {
  contactEmail: String
  contactName: String
  contactPhone: String
  name: String
  notes: String
  vendorId: String!
}

type VpnTunnelState {
  """
  1 = at least one tunnel up, 0 = both down, absent = no datapoint in the window.
  """
  tunnelState: Float
  vpnId: String!
}
`;
